import React from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowDown, ArrowUp, Edit2, Plus, Trash2 } from 'lucide-react';

import { InputFieldDef } from '../../../types/admin';
import { orderFields, rewriteOrder } from './inputFieldOrder';
import { InputFieldDialog } from './InputFieldDialog';

interface InputFieldListEditorProps {
  /** Whole field list; only fields of `direction` are shown and edited, the rest are preserved. */
  fields: InputFieldDef[];
  onChange: (fields: InputFieldDef[]) => void;
  direction?: 'in' | 'out';
  disabled?: boolean;
}

const sideOf = (field: InputFieldDef) => field.direction ?? 'in';

export const InputFieldListEditor: React.FC<InputFieldListEditorProps> = ({
  fields,
  onChange,
  direction = 'in',
  disabled = false,
}) => {
  const isOut = direction === 'out';
  const [editingIndex, setEditingIndex] = React.useState<number | null>(null);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const sortedFields = React.useMemo(
    () => orderFields(fields.filter((field) => sideOf(field) === direction)),
    [fields, direction]
  );
  const otherFields = React.useMemo(
    () => fields.filter((field) => sideOf(field) !== direction),
    [fields, direction]
  );

  const emit = (subset: InputFieldDef[]) => {
    const rewritten = rewriteOrder(subset);
    onChange(isOut ? [...otherFields, ...rewritten] : [...rewritten, ...otherFields]);
  };

  const openAdd = () => {
    setEditingIndex(null);
    setIsDialogOpen(true);
  };

  const openEdit = (index: number) => {
    setEditingIndex(index);
    setIsDialogOpen(true);
  };

  const saveField = (field: InputFieldDef) => {
    emit(
      editingIndex === null
        ? [...sortedFields, field]
        : sortedFields.map((item, index) => (index === editingIndex ? field : item))
    );
    setIsDialogOpen(false);
  };

  const removeField = (index: number) => {
    emit(sortedFields.filter((_field, itemIndex) => itemIndex !== index));
  };

  const moveField = (index: number, step: -1 | 1) => {
    const next = [...sortedFields];
    const target = index + step;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    emit(next);
  };

  return (
    <Box>
      {sortedFields.length === 0 ? (
        <Alert severity="info" className="mb-3">
          {isOut ? 'No response fields configured.' : 'No input fields configured.'}
        </Alert>
      ) : (
        <Box className="overflow-x-auto rounded border border-slate-200 dark:border-slate-700">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{isOut ? 'Response path' : 'Name'}</TableCell>
                {isOut ? (
                  <TableCell>Meaning</TableCell>
                ) : (
                  <>
                    <TableCell>Key</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Required</TableCell>
                    <TableCell>Default</TableCell>
                    <TableCell>Pricing</TableCell>
                  </>
                )}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedFields.map((field, index) => (
                <TableRow key={`${field.key ?? field.name}-${index}`}>
                  <TableCell className="font-mono">{field.name}</TableCell>
                  {isOut ? (
                    <TableCell className="font-mono">{field.semantic ?? '-'}</TableCell>
                  ) : (
                    <>
                      <TableCell className="font-mono">{field.key || '-'}</TableCell>
                      <TableCell>{field.type}</TableCell>
                      <TableCell>{field.required ? 'Yes' : 'No'}</TableCell>
                      <TableCell>
                        <Typography variant="caption" className="break-all font-mono">
                          {formatDefault(field.default)}
                        </Typography>
                      </TableCell>
                      <TableCell>{field.pricingDimension ? 'Yes' : 'No'}</TableCell>
                    </>
                  )}
                  <TableCell align="right" className="whitespace-nowrap">
                    <Tooltip title="Move up">
                      <span>
                        <IconButton
                          size="small"
                          disabled={disabled || index === 0}
                          onClick={() => moveField(index, -1)}
                        >
                          <ArrowUp size={16} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Move down">
                      <span>
                        <IconButton
                          size="small"
                          disabled={disabled || index === sortedFields.length - 1}
                          onClick={() => moveField(index, 1)}
                        >
                          <ArrowDown size={16} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Edit field">
                      <span>
                        <IconButton
                          size="small"
                          disabled={disabled}
                          onClick={() => openEdit(index)}
                        >
                          <Edit2 size={16} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete field">
                      <span>
                        <IconButton
                          size="small"
                          disabled={disabled}
                          onClick={() => removeField(index)}
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <Button
        className="mt-3"
        variant="outlined"
        size="small"
        startIcon={<Plus size={16} />}
        disabled={disabled}
        onClick={openAdd}
      >
        {isOut ? 'Add Response Field' : 'Add Field'}
      </Button>

      <InputFieldDialog
        open={isDialogOpen}
        field={editingIndex === null ? null : sortedFields[editingIndex]}
        direction={direction}
        onClose={() => setIsDialogOpen(false)}
        onSave={saveField}
      />
    </Box>
  );
};

function formatDefault(value: unknown): string {
  if (value === undefined) return '-';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
}
